import { useMemo, useState } from "react";
import { Link } from "react-router";
import { Calendar, CheckSquare, Pencil } from "lucide-react";
import {
  Card,
  CardTitle,
  EmptyState,
  ErrorMessage,
  LoadingState,
  PageHeader,
  Subtitle,
  VButton,
  VCheckbox,
  VField,
  VSelect,
} from "@/components/ui";
import { useCurrentRole, useCurrentUser } from "@/domains/auth";
import { useTasks, useTasksByAssignee } from "@/domains/tasks/api/queries";
import { useUpdateTaskStatus } from "@/domains/tasks/api/mutations";
import type { CameraTask, TaskStatus } from "@/domains/tasks/api/types";
import { TaskCard } from "@/domains/tasks/components/taskCard";
import { TaskFormDialog } from "@/domains/tasks/components/taskFormDialog";
import { useCases } from "@/domains/cases";
import { useRequests } from "@/domains/requests";

type Bucket = { label: string; tasks: ReadonlyArray<CameraTask> };

function bucketTasks(tasks: ReadonlyArray<CameraTask>): ReadonlyArray<Bucket> {
  const today = "2026-05-11";
  const overdue: Array<CameraTask> = [];
  const todayList: Array<CameraTask> = [];
  const upcoming: Array<CameraTask> = [];
  const done: Array<CameraTask> = [];
  for (const t of tasks) {
    if (t.status === "done") {
      done.push(t);
      continue;
    }
    if (t.dueDate < today) overdue.push(t);
    else if (t.dueDate === today) todayList.push(t);
    else upcoming.push(t);
  }
  return [
    { label: "Te laat", tasks: overdue },
    { label: "Vandaag", tasks: todayList },
    { label: "Komende dagen", tasks: upcoming },
    { label: "Afgerond", tasks: done },
  ];
}

export function TaskListPage(): React.JSX.Element {
  const role = useCurrentRole();
  const currentUser = useCurrentUser();
  const all = useTasks();
  const mine = useTasksByAssignee(currentUser.id);
  const [onlyMine, setOnlyMine] = useState(role !== "manager");
  const isPartner = role === "partner";
  const source = onlyMine || isPartner ? mine : all;
  const updateStatus = useUpdateTaskStatus();
  const [editTask, setEditTask] = useState<CameraTask | null>(null);

  const { data: cases } = useCases();
  const { data: requests } = useRequests();
  const [caseFilter, setCaseFilter] = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");

  const requesterIdByCaseId = useMemo(() => {
    const requestById = new Map((requests ?? []).map((r) => [r.id, r] as const));
    const map = new Map<string, string>();
    for (const c of cases ?? []) {
      const req = requestById.get(c.requestId);
      if (req) map.set(c.id, req.requesterId);
    }
    return map;
  }, [cases, requests]);

  const requesters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of requests ?? []) {
      if (!seen.has(r.requesterId)) {
        seen.set(r.requesterId, `${r.requester.firstName} ${r.requester.lastName}`);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [requests]);

  const filteredTasks = useMemo<ReadonlyArray<CameraTask>>(() => {
    const list = source.data ?? [];
    return list.filter((t) => {
      if (caseFilter && t.caseId !== caseFilter) return false;
      if (requesterFilter && requesterIdByCaseId.get(t.caseId) !== requesterFilter) {
        return false;
      }
      return true;
    });
  }, [source.data, caseFilter, requesterFilter, requesterIdByCaseId]);

  const buckets = useMemo<ReadonlyArray<Bucket>>(
    () => bucketTasks(filteredTasks),
    [filteredTasks],
  );

  const handleToggle = (task: CameraTask): void => {
    const next: TaskStatus = task.status === "done" ? "todo" : "done";
    updateStatus.mutate({ id: task.id, status: next });
  };

  const handleClearFilters = (): void => {
    setCaseFilter("");
    setRequesterFilter("");
  };

  const hasActiveFilters = caseFilter !== "" || requesterFilter !== "";

  return (
    <div className="space-y-6">
      <PageHeader
        title={isPartner ? "Mijn taken" : "Takenoverzicht"}
        subtitle={
          isPartner
            ? "Alle taken die aan jou zijn toegewezen door Politiezone Antwerpen."
            : "Alle openstaande, lopende en afgeronde taken."
        }
        actions={
          !isPartner ? (
            <div className="flex flex-wrap items-center gap-3">
              <VCheckbox
                id="onlyMine"
                checked={onlyMine}
                onCheckedChange={(c) => setOnlyMine(c === true)}
                label="Toon alleen mijn taken"
              />
              <VButton variant="outline" asChild>
                <Link to="/tasks">
                  <Calendar className="size-4" aria-hidden />
                  Kalender-view
                </Link>
              </VButton>
            </div>
          ) : null
        }
      />

      {!isPartner ? (
        <Card padding="md" className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[1fr,1fr,auto] sm:items-end">
            <VField label="Filter op dossier" htmlFor="taskCaseFilter">
              <VSelect
                id="taskCaseFilter"
                value={caseFilter}
                onChange={(e) => setCaseFilter(e.target.value)}
              >
                <option value="">Alle dossiers</option>
                {(cases ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.number} — {c.title}
                  </option>
                ))}
              </VSelect>
            </VField>
            <VField label="Filter op aanvrager" htmlFor="taskRequesterFilter">
              <VSelect
                id="taskRequesterFilter"
                value={requesterFilter}
                onChange={(e) => setRequesterFilter(e.target.value)}
              >
                <option value="">Alle aanvragers</option>
                {requesters.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </VSelect>
            </VField>
            <VButton
              variant="ghost"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Filters wissen
            </VButton>
          </div>
        </Card>
      ) : null}

      {source.isPending ? (
        <LoadingState label="Taken laden…" />
      ) : source.isError ? (
        <ErrorMessage message="Kon taken niet ophalen." />
      ) : filteredTasks.length === 0 ? (
        <EmptyState
          title="Geen taken"
          description={
            hasActiveFilters
              ? "Geen taken die aan deze filters voldoen."
              : "Er zijn momenteel geen taken."
          }
        />
      ) : (
        <div className="space-y-4">
          {buckets.map((bucket) =>
            bucket.tasks.length === 0 ? null : (
              <Card key={bucket.label} padding="lg" className="space-y-3">
                <div className="flex items-center justify-between">
                  <CardTitle>{bucket.label}</CardTitle>
                  <Subtitle>{bucket.tasks.length} taak/taken</Subtitle>
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {bucket.tasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-2">
                      <VButton
                        variant="ghost"
                        size="icon"
                        aria-label={
                          task.status === "done" ? "Afvinken ongedaan" : "Markeer als klaar"
                        }
                        onClick={() => handleToggle(task)}
                        icon={
                          <CheckSquare
                            className={
                              task.status === "done"
                                ? "size-5 text-success-600"
                                : "size-5 text-muted-foreground"
                            }
                            aria-hidden
                          />
                        }
                      />
                      <TaskCard task={task} className="flex-1" />
                      <VButton
                        variant="ghost"
                        size="icon"
                        aria-label="Bewerken"
                        onClick={() => setEditTask(task)}
                        icon={<Pencil className="size-4 text-muted-foreground" aria-hidden />}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            ),
          )}
        </div>
      )}

      {editTask ? (
        <TaskFormDialog
          open
          onOpenChange={(open) => {
            if (!open) setEditTask(null);
          }}
          mode={{ kind: "edit", task: editTask }}
        />
      ) : null}
    </div>
  );
}
